import React, { useState, useEffect } from "react";
import axios from "axios";
import { server } from "../../server";
import useAuth from "../../useAuth";
import {
  FaWallet,
  FaCalendarDay,
  FaCalendarAlt,
  FaClock,
  FaExclamationTriangle,
  FaReceipt,
  FaCheckCircle,
  FaMoneyBillWave,
  FaChartLine,
  FaCreditCard,
  FaExchangeAlt,
  FaFileInvoiceDollar,
  FaPlus,
  FaUserGraduate,
  FaSyncAlt,
  FaChartBar,
  FaChartPie,
  FaArrowUp,
  FaArrowDown,
  FaFileAlt,
  FaFilter,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const MODE_COLORS = [
  "#10B981", // Emerald
  "#6366F1", // Indigo
  "#3B82F6", // Blue
  "#F59E0B", // Amber
  "#EC4899", // Pink
  "#8B5CF6", // Purple
];

const MODE_GRADIENTS = [
  ["#10B981", "#6EE7B7"],
  ["#6366F1", "#A5B4FC"],
  ["#3B82F6", "#93C5FD"],
  ["#F59E0B", "#FDE68A"],
  ["#EC4899", "#F9A8D4"],
  ["#8B5CF6", "#C4B5FD"],
];

const cardVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.4,
      ease: "easeOut",
    },
  }),
};

const StatCard = ({
  icon: Icon,
  title,
  value,
  subtitle,
  color,
  gradient,
  trend,
  index,
}) => (
  <motion.div
    custom={index}
    initial="hidden"
    animate="visible"
    variants={cardVariants}
    className="relative bg-white dark:bg-slate-900 rounded-xl p-3.5 border border-slate-200/80 dark:border-slate-800 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group"
  >
    <div
      className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${gradient} rounded-t-xl`}
    />
    <div className="absolute -top-12 -right-12 w-28 h-28 rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-500 bg-gradient-to-br from-white to-transparent" />

    <div className="flex items-center justify-between mb-2">
      <div
        className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center text-white shadow-md transform group-hover:scale-105 transition-transform duration-300`}
      >
        <Icon className="text-sm" />
      </div>
      {trend !== undefined && trend !== null && (
        <div
          className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold ${
            trend >= 0
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
              : "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400"
          }`}
        >
          {trend >= 0 ? <FaArrowUp size={8} /> : <FaArrowDown size={8} />}
          {Math.abs(trend)}%
        </div>
      )}
    </div>

    <div className="space-y-0.5">
      <div className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight">
        {value}
      </div>
      <div className="text-xs font-bold text-slate-600 dark:text-slate-300">
        {title}
      </div>
      {subtitle && (
        <div className="text-[11px] font-medium text-slate-400 dark:text-slate-500 truncate">
          {subtitle}
        </div>
      )}
    </div>
  </motion.div>
);

export default function FeeDashboardPage() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const navigate = useNavigate();

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${server}/fee-dashboard/metrics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setMetrics(res.data.data);
      }
    } catch (error) {
      toast.error("Failed to load Fee Dashboard metrics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchMetrics();
  }, [token]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Calculate efficiency percentage
  const totalAssignedOrCurrent =
    (metrics?.monthlyCollection || 0) + (metrics?.pendingCollection || 0);
  const collectionEfficiency =
    totalAssignedOrCurrent > 0
      ? Math.min(
          100,
          Math.round(
            ((metrics?.monthlyCollection || 0) / totalAssignedOrCurrent) * 100,
          ),
        )
      : 100;

  return (
    <div className="p-3 md:p-4 max-w-[1440px] mx-auto min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      <div className="space-y-3.5">
        {/* ════════════════ HEADER SECTION ════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl p-3.5 md:p-4 rounded-xl border border-slate-200/50 dark:border-slate-800/50 shadow-md"
        >
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-md shadow-emerald-500/20">
              <FaWallet className="text-lg" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-extrabold bg-gradient-to-r from-slate-900 to-slate-600 dark:from-white dark:to-slate-400 bg-clip-text text-transparent leading-tight">
                Fee Management Dashboard
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                Real-time financial analytics, fee collections, outstanding
                dues, and receipt metrics
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={fetchMetrics}
              disabled={loading}
              className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-lg transition border border-slate-200 dark:border-slate-700 flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            >
              <FaSyncAlt
                className={loading ? "animate-spin text-xs" : "text-xs"}
              />
              Refresh
            </button>

            <button
              onClick={() => navigate("/my/fees/collect")}
              className="px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-xs font-bold rounded-lg shadow-sm shadow-emerald-500/20 transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <FaPlus className="text-xs" /> Collect Fee
            </button>

            <button
              onClick={() => navigate("/my/fees/assign")}
              className="px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white text-xs font-bold rounded-lg shadow-sm shadow-indigo-500/20 transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <FaFileInvoiceDollar className="text-xs" /> Assign Fee
            </button>

            <button
              onClick={() => navigate("/my/fees/reports")}
              className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white text-xs font-bold rounded-lg shadow-sm shadow-purple-500/20 transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <FaFileAlt className="text-xs" /> Fee Reports
            </button>
          </div>
        </motion.div>

        {loading ? (
          <div className="flex flex-col items-center justify-center p-12 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-xl border border-slate-200/50 dark:border-slate-800/50 shadow-md">
            <div className="relative">
              <div className="w-12 h-12 border-4 border-emerald-200 dark:border-emerald-900/30 rounded-full animate-spin border-t-emerald-500" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-6 h-6 border-4 border-emerald-300 dark:border-emerald-800/30 rounded-full animate-ping" />
              </div>
            </div>
            <p className="mt-4 text-slate-500 dark:text-slate-400 text-xs font-semibold animate-pulse">
              Loading Fee Intelligence Analytics...
            </p>
          </div>
        ) : (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="space-y-3.5"
            >
              {/* ════════ TOP KPI STAT CARDS ════════ */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard
                  index={0}
                  icon={FaCalendarDay}
                  title="Today's Collection"
                  value={formatCurrency(metrics?.todaysCollection)}
                  subtitle={`${metrics?.todaysReceipts || 0} receipts generated today`}
                  color="bg-gradient-to-br from-emerald-500 to-emerald-600"
                  gradient="from-emerald-500 to-emerald-600"
                  trend={4.2}
                />

                <StatCard
                  index={1}
                  icon={FaClock}
                  title="Yesterday Collection"
                  value={formatCurrency(metrics?.yesterdaysCollection)}
                  subtitle="Verified daily collection"
                  color="bg-gradient-to-br from-blue-500 to-blue-600"
                  gradient="from-blue-500 to-blue-600"
                  trend={1.8}
                />

                <StatCard
                  index={2}
                  icon={FaCalendarAlt}
                  title="Monthly Revenue"
                  value={formatCurrency(metrics?.monthlyCollection)}
                  subtitle="Current month collection aggregate"
                  color="bg-gradient-to-br from-indigo-500 to-indigo-600"
                  gradient="from-indigo-500 to-indigo-600"
                  trend={6.5}
                />

                <StatCard
                  index={3}
                  icon={FaExclamationTriangle}
                  title="Outstanding Dues"
                  value={formatCurrency(metrics?.pendingCollection)}
                  subtitle={`Overdue: ${formatCurrency(metrics?.overdueCollection)}`}
                  color="bg-gradient-to-br from-rose-500 to-rose-600"
                  gradient="from-rose-500 to-rose-600"
                  trend={-2.1}
                />
              </div>

              {/* ════════ SECONDARY METRICS ROW ════════ */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.3 }}
                  className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl p-3 rounded-xl border border-slate-200/50 dark:border-slate-800/50 shadow-sm flex items-center gap-2.5"
                >
                  <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg shadow-sm">
                    <FaReceipt className="text-xs" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Yearly Collection
                    </span>
                    <p className="font-extrabold text-slate-900 dark:text-white text-xs md:text-sm">
                      {formatCurrency(metrics?.yearlyCollection)}
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15, duration: 0.3 }}
                  className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl p-3 rounded-xl border border-slate-200/50 dark:border-slate-800/50 shadow-sm flex items-center gap-2.5"
                >
                  <div className="p-2 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-lg shadow-sm">
                    <FaCheckCircle className="text-xs" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Pending Approvals
                    </span>
                    <p className="font-extrabold text-slate-900 dark:text-white text-xs md:text-sm">
                      {metrics?.pendingApprovals || 0} Requests
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                  className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl p-3 rounded-xl border border-slate-200/50 dark:border-slate-800/50 shadow-sm flex items-center gap-2.5"
                >
                  <div className="p-2 bg-gradient-to-br from-rose-500 to-rose-600 text-white rounded-lg shadow-sm">
                    <FaExchangeAlt className="text-xs" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Total Refunded
                    </span>
                    <p className="font-extrabold text-slate-900 dark:text-white text-xs md:text-sm">
                      {formatCurrency(metrics?.totalRefunded)}
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25, duration: 0.3 }}
                  className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl p-3 rounded-xl border border-slate-200/50 dark:border-slate-800/50 shadow-sm flex items-center gap-2.5"
                >
                  <div className="p-2 bg-gradient-to-br from-slate-600 to-slate-700 text-white rounded-lg shadow-sm">
                    <FaReceipt className="text-xs" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Cancelled Receipts
                    </span>
                    <p className="font-extrabold text-slate-900 dark:text-white text-xs md:text-sm">
                      {metrics?.cancelledReceipts || 0} Receipts
                    </p>
                  </div>
                </motion.div>
              </div>

              {/* ════════ CHARTS SECTION 1: DAILY & PAYMENT MODE ════════ */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5">
                {/* Daily Collection Area Chart (14 Days) */}
                <motion.div
                  initial={{ opacity: 0, x: -15 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                  className="lg:col-span-8 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-xl p-3.5 md:p-4 border border-slate-200/50 dark:border-slate-800/50 shadow-md"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm md:text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <FaChartLine className="text-emerald-500 text-xs" />
                        Daily Collection Trend (Last 14 Days)
                      </h3>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        Real-time daily collection volume
                      </p>
                    </div>
                    <div className="flex items-center gap-1 bg-emerald-50 dark:bg-emerald-950/50 px-2 py-1 rounded-md text-[10px] font-bold text-emerald-600 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-500/20">
                      Collection Efficiency: {collectionEfficiency}%
                    </div>
                  </div>

                  <div className="h-[220px] w-full">
                    {metrics?.dailyChart && metrics.dailyChart.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={metrics.dailyChart}
                          margin={{ top: 5, right: 5, left: -20, bottom: 0 }}
                        >
                          <defs>
                            <linearGradient
                              id="emeraldArea"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="0%"
                                stopColor="#10B981"
                                stopOpacity={0.8}
                              />
                              <stop
                                offset="100%"
                                stopColor="#10B981"
                                stopOpacity={0.05}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#E2E8F0"
                            className="dark:opacity-10"
                          />
                          <XAxis
                            dataKey="date"
                            axisLine={false}
                            tickLine={false}
                            tick={{
                              fill: "#64748B",
                              fontSize: 10,
                              fontWeight: 600,
                            }}
                            dy={5}
                          />
                          <YAxis
                            tickFormatter={(value) =>
                              `₹${value >= 1000 ? (value / 1000).toFixed(0) + "k" : value}`
                            }
                            axisLine={false}
                            tickLine={false}
                            tick={{
                              fill: "#64748B",
                              fontSize: 10,
                              fontWeight: 600,
                            }}
                          />
                          <Tooltip
                            cursor={{
                              stroke: "#10B981",
                              strokeWidth: 1,
                              strokeDasharray: "3 3",
                            }}
                            contentStyle={{
                              backgroundColor: "#0F172A",
                              borderRadius: "10px",
                              border: "none",
                              color: "#FFFFFF",
                              boxShadow: "0 10px 15px -3px rgba(0,0,0,0.3)",
                              padding: "8px 12px",
                              fontSize: "11px",
                            }}
                            formatter={(value) => [
                              formatCurrency(value),
                              "Collection",
                            ]}
                          />
                          <Area
                            type="monotone"
                            dataKey="amount"
                            stroke="#10B981"
                            strokeWidth={2.5}
                            fillOpacity={1}
                            fill="url(#emeraldArea)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-400 italic text-xs">
                        No daily trend data available
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Payment Mode Distribution Donut Chart */}
                <motion.div
                  initial={{ opacity: 0, x: 15 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.25, duration: 0.4 }}
                  className="lg:col-span-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-xl p-3.5 md:p-4 border border-slate-200/50 dark:border-slate-800/50 shadow-md"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-sm md:text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <FaCreditCard className="text-indigo-500 text-xs" />
                        Payment Mode Breakdown
                      </h3>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        Collection distribution by channel
                      </p>
                    </div>
                  </div>

                  <div className="h-[220px] w-full">
                    {metrics?.modeDistribution &&
                    metrics.modeDistribution.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <defs>
                            {metrics.modeDistribution.map((entry, index) => (
                              <linearGradient
                                key={`mode-grad-${index}`}
                                id={`mode-grad-${index}`}
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                <stop
                                  offset="0%"
                                  stopColor={
                                    MODE_GRADIENTS[
                                      index % MODE_GRADIENTS.length
                                    ][0]
                                  }
                                />
                                <stop
                                  offset="100%"
                                  stopColor={
                                    MODE_GRADIENTS[
                                      index % MODE_GRADIENTS.length
                                    ][1]
                                  }
                                />
                              </linearGradient>
                            ))}
                          </defs>
                          <Pie
                            data={metrics.modeDistribution.map((item) => ({
                              ...item,
                              name: item.mode,
                              value: Number(item.total) || 0,
                            }))}
                            cx="50%"
                            cy="45%"
                            innerRadius={42}
                            outerRadius={68}
                            paddingAngle={3}
                            dataKey="value"
                            stroke="none"
                          >
                            {metrics.modeDistribution.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={`url(#mode-grad-${index})`}
                                className="hover:opacity-80 transition-opacity cursor-pointer"
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value, name, props) => {
                              const totalAll =
                                metrics.modeDistribution.reduce(
                                  (acc, curr) => acc + curr.total,
                                  0,
                                ) || 1;
                              const pct = Math.round((value / totalAll) * 100);
                              return [
                                `${formatCurrency(value)} (${pct}%)`,
                                props.payload.name || "Mode",
                              ];
                            }}
                            contentStyle={{
                              backgroundColor: "#0F172A",
                              borderRadius: "8px",
                              border: "none",
                              color: "#FFFFFF",
                              boxShadow: "0 10px 15px -3px rgba(0,0,0,0.3)",
                              fontSize: "10px",
                              fontWeight: "bold",
                              padding: "6px 10px",
                            }}
                          />
                          <Legend
                            iconType="circle"
                            formatter={(value, entry) => {
                              const totalAll =
                                metrics.modeDistribution.reduce(
                                  (acc, curr) => acc + curr.total,
                                  0,
                                ) || 1;
                              const val = entry.payload?.value || 0;
                              const pct = Math.round((val / totalAll) * 100);
                              return (
                                <span className="text-slate-700 dark:text-slate-300 font-semibold text-[10px]">
                                  {value} ({pct}%)
                                </span>
                              );
                            }}
                            wrapperStyle={{
                              fontSize: "10px",
                              fontWeight: 600,
                              paddingTop: "5px",
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-400 italic text-xs">
                        No payment mode data available
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>

              {/* ════════ CHARTS SECTION 2: MONTHLY TREND (6 MONTHS) & QUICK ACTIONS ════════ */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5">
                {/* 6 Months Collection Trend BarChart */}
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.4 }}
                  className="lg:col-span-8 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-xl p-3.5 md:p-4 border border-slate-200/50 dark:border-slate-800/50 shadow-md"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm md:text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <FaChartBar className="text-indigo-500 text-xs" />
                        6-Month Collection Trend
                      </h3>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        Monthly financial collection performance
                      </p>
                    </div>
                  </div>

                  <div className="h-[210px] w-full">
                    {metrics?.monthlyChart &&
                    metrics.monthlyChart.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={metrics.monthlyChart}
                          margin={{ top: 5, right: 5, left: -20, bottom: 0 }}
                        >
                          <defs>
                            <linearGradient
                              id="indigoBar"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="0%"
                                stopColor="#6366F1"
                                stopOpacity={0.85}
                              />
                              <stop
                                offset="100%"
                                stopColor="#6366F1"
                                stopOpacity={0.4}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#E2E8F0"
                            className="dark:opacity-10"
                          />
                          <XAxis
                            dataKey="month"
                            axisLine={false}
                            tickLine={false}
                            tick={{
                              fill: "#64748B",
                              fontSize: 10,
                              fontWeight: 600,
                            }}
                            dy={5}
                          />
                          <YAxis
                            tickFormatter={(value) =>
                              `₹${value >= 1000 ? (value / 1000).toFixed(0) + "k" : value}`
                            }
                            axisLine={false}
                            tickLine={false}
                            tick={{
                              fill: "#64748B",
                              fontSize: 10,
                              fontWeight: 600,
                            }}
                          />
                          <Tooltip
                            cursor={{ fill: "rgba(226, 232, 240, 0.2)" }}
                            contentStyle={{
                              backgroundColor: "#0F172A",
                              borderRadius: "10px",
                              border: "none",
                              color: "#FFFFFF",
                              boxShadow: "0 10px 15px -3px rgba(0,0,0,0.3)",
                              padding: "8px 12px",
                              fontSize: "11px",
                            }}
                            formatter={(value) => [
                              formatCurrency(value),
                              "Monthly Collection",
                            ]}
                          />
                          <Bar
                            name="Collection"
                            dataKey="amount"
                            fill="url(#indigoBar)"
                            radius={[4, 4, 0, 0]}
                            maxBarSize={36}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-400 italic text-xs">
                        No 6-month trend data available
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Institutional Quick Action Panel */}
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35, duration: 0.4 }}
                  className="lg:col-span-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-xl p-3.5 md:p-4 border border-slate-200/50 dark:border-slate-800/50 shadow-md flex flex-col justify-between"
                >
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1 flex items-center gap-2">
                      <FaMoneyBillWave className="text-emerald-500 text-xs" />
                      Institutional Insights
                    </h3>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-3">
                      Quick management operations
                    </p>

                    <div className="space-y-2">
                      <div
                        onClick={() => navigate("/my/fees/upcoming-dues")}
                        className="p-2.5 rounded-lg bg-gradient-to-r from-amber-50 to-amber-100/50 dark:from-amber-950/40 dark:to-amber-950/20 border border-amber-200/50 dark:border-amber-700/30 flex items-center justify-between cursor-pointer hover:shadow-sm transition"
                      >
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-amber-500 text-white rounded-md text-xs">
                            <FaExclamationTriangle />
                          </div>
                          <div>
                            <div className="text-xs font-bold text-slate-900 dark:text-white">
                              Defaulter & Due Fees
                            </div>
                            <div className="text-[10px] text-slate-500 dark:text-slate-400">
                              Overdue balance:{" "}
                              {formatCurrency(metrics?.overdueCollection)}
                            </div>
                          </div>
                        </div>
                        <span className="text-xs font-extrabold text-amber-600 dark:text-amber-400">
                          View →
                        </span>
                      </div>

                      <div
                        onClick={() => navigate("/my/fees/reports")}
                        className="p-2.5 rounded-lg bg-gradient-to-r from-purple-50 to-purple-100/50 dark:from-purple-950/40 dark:to-purple-950/20 border border-purple-200/50 dark:border-purple-700/30 flex items-center justify-between cursor-pointer hover:shadow-sm transition"
                      >
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-purple-500 text-white rounded-md text-xs">
                            <FaFileAlt />
                          </div>
                          <div>
                            <div className="text-xs font-bold text-slate-900 dark:text-white">
                              Fee Ledger & Reports
                            </div>
                            <div className="text-[10px] text-slate-500 dark:text-slate-400">
                              Generate custom financial reports
                            </div>
                          </div>
                        </div>
                        <span className="text-xs font-extrabold text-purple-600 dark:text-purple-400">
                          View →
                        </span>
                      </div>

                      <div
                        onClick={() => navigate("/my/fees/collect")}
                        className="p-2.5 rounded-lg bg-gradient-to-r from-emerald-50 to-emerald-100/50 dark:from-emerald-950/40 dark:to-emerald-950/20 border border-emerald-200/50 dark:border-emerald-700/30 flex items-center justify-between cursor-pointer hover:shadow-sm transition"
                      >
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-emerald-500 text-white rounded-md text-xs">
                            <FaPlus />
                          </div>
                          <div>
                            <div className="text-xs font-bold text-slate-900 dark:text-white">
                              Collect Student Fee
                            </div>
                            <div className="text-[10px] text-slate-500 dark:text-slate-400">
                              Issue instant print receipt
                            </div>
                          </div>
                        </div>
                        <span className="text-xs font-extrabold text-emerald-600 dark:text-emerald-400">
                          Collect →
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-800 text-[10px] text-slate-400 flex justify-between items-center font-medium">
                    <span>Audit Status: Verified</span>
                    <span>Updated Live</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
